import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger';
export type ButtonSize = 'md' | 'lg';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Full width. The ops shell wants this almost everywhere. */
  block?: boolean;
  /** Shown instead of children while an action is in flight. */
  busyLabel?: string;
  busy?: boolean;
  children: ReactNode;
};

const base =
  'inline-flex items-center justify-center gap-2 rounded font-body font-medium ' +
  'min-h-tap px-5 transition-colors ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-onAccent hover:opacity-90',
  secondary: 'bg-surface text-ink border border-line hover:border-accent',
  quiet: 'bg-transparent text-accent underline underline-offset-4',
  danger: 'bg-danger text-surface hover:opacity-90',
};

const sizes: Record<ButtonSize, string> = {
  md: 'text-body',
  lg: 'text-body py-4 text-[1.1em]',
};

/**
 * A button says what happens. Never "OK", never "Submit".
 * Tap target never drops below the shell's floor (44 px guest, 48 px ops).
 */
export function Button({
  variant = 'primary',
  size = 'md',
  block = false,
  busy = false,
  busyLabel,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={cn(base, variants[variant], sizes[size], block && 'w-full', className)}
      {...rest}
    >
      {busy && busyLabel ? busyLabel : children}
    </button>
  );
}
